// Pure pieces of the what-if screens (spec 03 §4.10, D-471, D-472, D-474): the
// two addresses, the builder's hypothesis rows as typed and their check, the
// create body, a duplicate's hypotheses, the promote confirmation's lists, the
// sandbox's what-if order labels, and the refusals in words.

import { RequestError } from "../../../../lib/request";
import { addDaysIso } from "../../../../components/bucket-grid-paste";
import { wallToUtcMs } from "./calendar-lib";
import type {
  ScenarioCreateRequest,
  ScenarioHypothesis,
  ScenarioHypothesisInput,
  ScenarioHypothesisKind,
  ScenarioStatus,
} from "../hooks/scenario-queries";
import {
  PERSISTABLE_KINDS,
  SCENARIO_MAX_WINDOW_DAYS,
  SCENARIO_NAME_MAX,
  SCENARIO_STATUSES,
} from "../hooks/scenario-queries";

const DATE = /^\d{4}-\d{2}-\d{2}$/u;
const LOCAL = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})$/u;

function date(v: unknown): string | undefined {
  if (typeof v !== "string" || !DATE.test(v)) return undefined;
  const t = Date.parse(`${v}T00:00:00Z`);
  if (Number.isNaN(t)) return undefined;
  return new Date(t).toISOString().slice(0, 10) === v ? v : undefined;
}

// ---- the addresses ------------------------------------------------------------

/** The list's address: the status filter (row 81's one filter). */
export interface ScenariosSearch {
  readonly status?: ScenarioStatus | undefined;
}

export function validateScenariosSearch(search: Record<string, unknown>): ScenariosSearch {
  const status = search["status"];
  return typeof status === "string" && (SCENARIO_STATUSES as readonly string[]).includes(status)
    ? { status: status as ScenarioStatus }
    : {};
}

/** The result's address: the first date its lanes show. */
export interface ScenarioResultSearch {
  readonly from?: string | undefined;
}

export function validateScenarioResultSearch(
  search: Record<string, unknown>,
): ScenarioResultSearch {
  const from = date(search["from"]);
  return from !== undefined ? { from } : {};
}

// ---- the builder's rows -------------------------------------------------------

/**
 * One hypothesis as the builder holds it: every field the person types as
 * text, instants as `YYYY-MM-DDTHH:MM` in the facility's zone (a
 * `datetime-local` value read as the facility's wall clock), so a half-typed
 * row survives a re-render and the check speaks to the field.
 */
export type HypothesisDraft =
  | {
      readonly kind: "add_shift";
      readonly workCenterId: string;
      readonly starts: string;
      readonly ends: string;
      readonly capacityFactor: string;
    }
  | {
      readonly kind: "machine_down";
      readonly workCenterId: string;
      readonly starts: string;
      readonly ends: string;
    }
  | {
      readonly kind: "add_order";
      readonly productId: string;
      readonly productLabel: string;
      readonly qty: string;
      readonly dueDate: string;
      readonly priority: string;
    }
  | {
      readonly kind: "change_efficiency";
      readonly workCenterId: string;
      readonly shiftCode: string;
      readonly efficiencyPct: string;
    }
  | {
      readonly kind: "drop_order";
      readonly orderId: string;
      readonly lineId: string;
    };

/** A builder row's field a check can name. */
export type DraftField =
  | "workCenterId"
  | "starts"
  | "ends"
  | "capacityFactor"
  | "productId"
  | "qty"
  | "dueDate"
  | "shiftCode"
  | "efficiencyPct"
  | "orderId"
  | "lineId";

/** Why a field is refused; each is a key under `planning.scenarios.builder.issue`. */
export type DraftIssue = "required" | "window" | "factor" | "qty" | "efficiency" | "date";

/**
 * A new row of a kind, with times that read sensibly from the facility's
 * today: a shift tomorrow from 08:00 to 16:00, a machine down from tomorrow
 * for five days, an order due in two weeks.
 */
export function emptyDraft(kind: ScenarioHypothesisKind, today: string): HypothesisDraft {
  const tomorrow = addDaysIso(today, 1);
  switch (kind) {
    case "add_shift":
      return {
        kind,
        workCenterId: "",
        starts: `${tomorrow}T08:00`,
        ends: `${tomorrow}T16:00`,
        capacityFactor: "1.00",
      };
    case "machine_down":
      return {
        kind,
        workCenterId: "",
        starts: `${tomorrow}T00:00`,
        ends: `${addDaysIso(tomorrow, 5)}T00:00`,
      };
    case "add_order":
      return {
        kind,
        productId: "",
        productLabel: "",
        qty: "",
        dueDate: addDaysIso(today, 14),
        priority: "3",
      };
    case "change_efficiency":
      return { kind, workCenterId: "", shiftCode: "", efficiencyPct: "100" };
    case "drop_order":
      return { kind, orderId: "", lineId: "" };
  }
}

/** A typed decimal with a comma or a dot, and no grouping, as the API wants it. */
function decimalText(text: string): string | undefined {
  const t = text.trim().replace(/\s/gu, "").replace(",", ".");
  return /^\d+(\.\d+)?$/u.test(t) ? t : undefined;
}

/** A capacity factor `0.01` to `3.00`, at most two places, or undefined. */
export function parseFactor(text: string): string | undefined {
  const t = decimalText(text);
  if (t === undefined || !/^\d(\.\d{1,2})?$/u.test(t)) return undefined;
  const n = Number(t);
  return n >= 0.01 && n <= 3 ? t : undefined;
}

/** A positive quantity with at most three places, or undefined. */
export function parseQty(text: string): string | undefined {
  const t = decimalText(text);
  if (t === undefined || !/^\d{1,11}(\.\d{1,3})?$/u.test(t)) return undefined;
  return Number(t) > 0 ? t : undefined;
}

/** A local `YYYY-MM-DDTHH:MM` as an instant in the facility's zone, or undefined. */
export function localToInstant(local: string, timezone: string): string | undefined {
  const m = LOCAL.exec(local.trim());
  if (m === null || date(m[1]) === undefined) return undefined;
  const ms = wallToUtcMs(m[1] ?? "", m[2] ?? "", timezone);
  return Number.isNaN(ms) ? undefined : new Date(ms).toISOString();
}

export type DraftCheck =
  | { readonly ok: true; readonly input: ScenarioHypothesisInput }
  | { readonly ok: false; readonly issues: Partial<Record<DraftField, DraftIssue>> };

function windowIssues(
  starts: string,
  ends: string,
  timezone: string,
  issues: Partial<Record<DraftField, DraftIssue>>,
): { starts_at: string; ends_at: string } | undefined {
  const s = localToInstant(starts, timezone);
  const e = localToInstant(ends, timezone);
  if (s === undefined) issues.starts = "required";
  if (e === undefined) issues.ends = "required";
  if (s === undefined || e === undefined) return undefined;
  const span = Date.parse(e) - Date.parse(s);
  if (span <= 0 || span > SCENARIO_MAX_WINDOW_DAYS * 86_400_000) {
    issues.ends = "window";
    return undefined;
  }
  return { starts_at: s, ends_at: e };
}

/**
 * Checks one row as the contract will (`ScenarioHypothesisInput`) and builds
 * its create body, so a refusal is named on its field before anything is
 * sent. The server still decides; its 404s name the row by index.
 */
export function checkDraft(draft: HypothesisDraft, timezone: string): DraftCheck {
  const issues: Partial<Record<DraftField, DraftIssue>> = {};
  const fail = (): DraftCheck => ({ ok: false, issues });
  switch (draft.kind) {
    case "add_shift": {
      if (draft.workCenterId === "") issues.workCenterId = "required";
      const window = windowIssues(draft.starts, draft.ends, timezone, issues);
      const factor = parseFactor(draft.capacityFactor);
      if (factor === undefined) issues.capacityFactor = "factor";
      if (window === undefined || factor === undefined || draft.workCenterId === "") return fail();
      return {
        ok: true,
        input: {
          kind: "add_shift",
          work_center_id: draft.workCenterId,
          ...window,
          capacity_factor: factor,
        },
      };
    }
    case "machine_down": {
      if (draft.workCenterId === "") issues.workCenterId = "required";
      const window = windowIssues(draft.starts, draft.ends, timezone, issues);
      if (window === undefined || draft.workCenterId === "") return fail();
      return {
        ok: true,
        input: { kind: "machine_down", work_center_id: draft.workCenterId, ...window },
      };
    }
    case "add_order": {
      if (draft.productId === "") issues.productId = "required";
      const qty = parseQty(draft.qty);
      if (qty === undefined) issues.qty = "qty";
      const due = date(draft.dueDate);
      if (due === undefined) issues.dueDate = "date";
      const priority = Number(draft.priority);
      if (draft.productId === "" || qty === undefined || due === undefined) return fail();
      return {
        ok: true,
        input: {
          kind: "add_order",
          product_id: draft.productId,
          qty,
          due_date: due,
          priority: Number.isInteger(priority) && priority >= 1 && priority <= 5 ? priority : 3,
        },
      };
    }
    case "change_efficiency": {
      if (draft.workCenterId === "") issues.workCenterId = "required";
      if (draft.shiftCode.trim() === "") issues.shiftCode = "required";
      const pct = /^\d{1,3}$/u.test(draft.efficiencyPct.trim())
        ? Number(draft.efficiencyPct.trim())
        : Number.NaN;
      if (!(pct >= 10 && pct <= 200)) issues.efficiencyPct = "efficiency";
      if (Object.keys(issues).length > 0) return fail();
      return {
        ok: true,
        input: {
          kind: "change_efficiency",
          work_center_id: draft.workCenterId,
          shift_code: draft.shiftCode.trim(),
          efficiency_pct: pct,
        },
      };
    }
    case "drop_order": {
      if (draft.orderId === "") issues.orderId = "required";
      if (draft.lineId === "") issues.lineId = "required";
      if (Object.keys(issues).length > 0) return fail();
      return { ok: true, input: { kind: "drop_order", sales_order_line_id: draft.lineId } };
    }
  }
}

export type BuilderCheck =
  | { readonly ok: true; readonly body: ScenarioCreateRequest }
  | {
      readonly ok: false;
      readonly nameIssue: boolean;
      readonly emptyIssue: boolean;
      readonly rows: ReadonlyMap<number, Partial<Record<DraftField, DraftIssue>>>;
    };

/** Checks the whole builder and builds row 80's body. */
export function checkBuilder(
  facilityId: string,
  name: string,
  drafts: readonly HypothesisDraft[],
  timezone: string,
): BuilderCheck {
  const trimmed = name.trim();
  const nameIssue = trimmed.length === 0 || trimmed.length > SCENARIO_NAME_MAX;
  const rows = new Map<number, Partial<Record<DraftField, DraftIssue>>>();
  const inputs: ScenarioHypothesisInput[] = [];
  drafts.forEach((d, i) => {
    const r = checkDraft(d, timezone);
    if (r.ok) inputs.push(r.input);
    else rows.set(i, r.issues);
  });
  const emptyIssue = drafts.length === 0;
  if (nameIssue || emptyIssue || rows.size > 0) return { ok: false, nameIssue, emptyIssue, rows };
  return { ok: true, body: { facility_id: facilityId, name: trimmed, hypothesis: inputs } };
}

// ---- a duplicate ---------------------------------------------------------------

/** A stored hypothesis as a create names it again: the same fields, no `persistable`. */
export function hypothesisInputOf(h: ScenarioHypothesis): ScenarioHypothesisInput {
  switch (h.kind) {
    case "add_shift":
      return {
        kind: h.kind,
        work_center_id: h.work_center_id,
        starts_at: h.starts_at,
        ends_at: h.ends_at,
        capacity_factor: h.capacity_factor,
      };
    case "machine_down":
      return {
        kind: h.kind,
        work_center_id: h.work_center_id,
        starts_at: h.starts_at,
        ends_at: h.ends_at,
      };
    case "add_order":
      return {
        kind: h.kind,
        product_id: h.product_id,
        qty: h.qty,
        due_date: h.due_date,
        priority: h.priority,
      };
    case "change_efficiency":
      return {
        kind: h.kind,
        work_center_id: h.work_center_id,
        shift_code: h.shift_code,
        efficiency_pct: h.efficiency_pct,
      };
    case "drop_order":
      return { kind: h.kind, sales_order_line_id: h.sales_order_line_id };
  }
}

/** A duplicate's name: the original's with the locale's suffix, held to the 120 limit. */
export function copyName(name: string, suffixed: (name: string) => string): string {
  const full = suffixed(name);
  if (full.length <= SCENARIO_NAME_MAX) return full;
  const over = full.length - SCENARIO_NAME_MAX;
  return suffixed(name.slice(0, Math.max(1, name.length - over - 1)).trimEnd() + "…");
}

// ---- promote ---------------------------------------------------------------------

/** Whether any hypothesis is one promote writes (D-472 ruling 3). */
export function hasPersistable(hypotheses: readonly Pick<ScenarioHypothesis, "kind">[]): boolean {
  return hypotheses.some((h) => PERSISTABLE_KINDS.has(h.kind));
}

/** The three codes row 84 needs at the facility (D-472 ruling 3). */
export const PROMOTE_CODES = [
  "planning.scenario.promote",
  "planning.calendar.manage",
  "planning.cost.read",
] as const;

/** The promote confirmation's two lists, each hypothesis with its index. */
export function promotePlan(hypotheses: readonly ScenarioHypothesis[]): {
  readonly writes: readonly { readonly index: number; readonly hypothesis: ScenarioHypothesis }[];
  readonly skips: readonly { readonly index: number; readonly hypothesis: ScenarioHypothesis }[];
} {
  const writes: { index: number; hypothesis: ScenarioHypothesis }[] = [];
  const skips: { index: number; hypothesis: ScenarioHypothesis }[] = [];
  hypotheses.forEach((hypothesis, index) => {
    (PERSISTABLE_KINDS.has(hypothesis.kind) ? writes : skips).push({ index, hypothesis });
  });
  return { writes, skips };
}

/** The key under `planning.scenarios.codeName` that names a code in words. */
export function codeNameKey(code: string): string | undefined {
  switch (code) {
    case "planning.scenario.promote":
      return "scenarioPromote";
    case "planning.calendar.manage":
      return "calendarManage";
    case "planning.cost.read":
      return "costRead";
    default:
      return undefined;
  }
}

// ---- the sandbox board -----------------------------------------------------------

/** The `n` of a sandbox `add_order` operation's `WHATIF-<n>`, or undefined. */
export function whatIfOrderNumber(orderNo: string | null): number | undefined {
  if (orderNo === null) return undefined;
  const m = /^WHATIF-(\d{1,4})$/u.exec(orderNo);
  return m === null ? undefined : Number(m[1]);
}

/** A signed figure as the KPI cards print it: "+3", "−1.5", "0". */
export function signedText(n: number, locale: string, digits = 0): string {
  const abs = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  }).format(Math.abs(n));
  if (n > 0) return `+${abs}`;
  if (n < 0) return `−${abs}`;
  return abs;
}

/** Elapsed time since an instant, as `m:ss`, for the solving state's timer. */
export function elapsedText(sinceIso: string, nowMs: number): string {
  const since = Date.parse(sinceIso);
  const seconds = Number.isNaN(since) ? 0 : Math.max(0, Math.floor((nowMs - since) / 1000));
  return `${String(Math.floor(seconds / 60))}:${String(seconds % 60).padStart(2, "0")}`;
}

// ---- refusals --------------------------------------------------------------------

/** A refusal of rows 80 to 85 in words: a key under `planning.scenarios.problem` and its values. */
export interface ScenarioProblemText {
  readonly key: string;
  readonly values: Readonly<Record<string, string | number>>;
}

const PROBLEM_CODES: ReadonlySet<string> = new Set([
  "SCENARIO_LIMIT_REACHED",
  "NO_PUBLISHED_SCHEDULE",
  "WORK_CENTER_NOT_FOUND",
  "PRODUCT_NOT_FOUND",
  "ORDER_LINE_NOT_FOUND",
  "SCENARIO_NOT_FOUND",
  "SCENARIO_NOT_SOLVABLE",
  "SCENARIO_NOT_SOLVED",
  "SCENARIO_CLOSED",
  "NOT_PERSISTABLE",
  "PERMISSION_DENIED",
  "FACILITY_NOT_FOUND",
]);

function extra(error: RequestError, key: string): unknown {
  return error.problem === undefined ? undefined : (error.problem as Record<string, unknown>)[key];
}

/**
 * The words for a failed write. A code the screens know names its own key;
 * the problem's extensions fill it (`limit`, `hypothesis_index` as the row's
 * number from 1, `scenario_status`, `required_codes`). Anything else is the
 * generic line with the server's title.
 */
export function scenarioProblem(error: unknown): ScenarioProblemText {
  if (!(error instanceof RequestError)) {
    return { key: "generic", values: { title: error instanceof Error ? error.message : "" } };
  }
  const code = error.code ?? "";
  if (!PROBLEM_CODES.has(code)) return { key: "generic", values: { title: error.message } };
  const values: Record<string, string | number> = {};
  const limit = extra(error, "limit");
  if (typeof limit === "number") values["limit"] = limit;
  const index = extra(error, "hypothesis_index");
  if (typeof index === "number") values["n"] = index + 1;
  const status = extra(error, "scenario_status");
  if (typeof status === "string") values["status"] = status;
  const codes = extra(error, "required_codes");
  if (Array.isArray(codes)) {
    values["codes"] = codes.filter((c): c is string => typeof c === "string").join(",");
  }
  return { key: code, values };
}
